// Gemeinsame UI-Bausteine des Pakets „Extras". Styling nur über Klassen `lfx-*` (extras.css, `--lf-*`-Variablen).
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { copyText, errorMessage } from './format';
import { useToastStore, type Toast } from './toast';
import './extras.css';

export function ExtrasPage(props: { title: string; testId: string; actions?: ReactNode; children: ReactNode; wide?: boolean }) {
  return (
    <main className={`lfx-page${props.wide ? ' lfx-page--wide' : ''}`} data-testid={props.testId}>
      <header className="lfx-page__head">
        <h1 className="lfx-page__title">{props.title}</h1>
        {props.actions && <div className="lfx-page__actions">{props.actions}</div>}
      </header>
      {props.children}
    </main>
  );
}

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' | 'ghost'; testId?: string };

export function Button({ variant = 'secondary', testId, className, type = 'button', ...rest }: ButtonProps) {
  return <button type={type} data-testid={testId} className={`lfx-btn lfx-btn--${variant}${className ? ` ${className}` : ''}`} {...rest} />;
}

/** Löschen o. ä. mit zweitem Klick bestätigen (kein window.confirm – testbar, barrierearm). */
export function ConfirmButton(props: { label: string; confirmLabel?: string; onConfirm: () => void; testId: string; disabled?: boolean; variant?: 'danger' | 'secondary' }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <Button
      variant={armed ? 'danger' : (props.variant ?? 'secondary')}
      testId={props.testId}
      data-armed={armed ? 'true' : 'false'}
      disabled={props.disabled}
      onClick={() => {
        if (armed) {
          setArmed(false);
          props.onConfirm();
        } else setArmed(true);
      }}
    >
      {armed ? (props.confirmLabel ?? 'Wirklich?') : props.label}
    </Button>
  );
}

export function Field(props: { label: string; hint?: ReactNode; children: (id: string) => ReactNode; error?: string | null }) {
  const id = useId();
  return (
    <div className="lfx-field">
      <label className="lfx-field__label" htmlFor={id}>
        {props.label}
      </label>
      {props.children(id)}
      {props.hint && <div className="lfx-field__hint">{props.hint}</div>}
      {props.error && (
        <div className="lfx-field__error" role="alert">
          {props.error}
        </div>
      )}
    </div>
  );
}

export function ErrorBox(props: { error: unknown; testId?: string }) {
  if (!props.error) return null;
  return (
    <div className="lfx-error" role="alert" data-testid={props.testId ?? 'extras-error'}>
      {errorMessage(props.error)}
    </div>
  );
}

export function Empty(props: { children: ReactNode; testId?: string }) {
  return (
    <div className="lfx-empty" data-testid={props.testId}>
      {props.children}
    </div>
  );
}

export function Loading(props: { testId?: string }) {
  return (
    <div className="lfx-loading" role="status" aria-live="polite" data-testid={props.testId ?? 'extras-loading'}>
      Lädt …
    </div>
  );
}

export function Badge(props: { children: ReactNode; tone?: 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' | 'mirror'; testId?: string; title?: string }) {
  return (
    <span className={`lfx-badge lfx-badge--${props.tone ?? 'neutral'}`} data-testid={props.testId} title={props.title}>
      {props.children}
    </span>
  );
}

export function ProgressBar(props: { value: number; max?: number; testId?: string; label?: string }) {
  const max = props.max ?? 100;
  const pct = max > 0 ? Math.max(0, Math.min(100, (props.value / max) * 100)) : 0;
  return (
    <div
      className="lfx-progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={props.value}
      aria-label={props.label}
      data-testid={props.testId}
    >
      <div className="lfx-progress__bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Modaler Dialog: Escape/Hintergrund schließt, Fokus auf den Dialog, Rückgabe des Fokus beim Schließen. */
export function Dialog(props: { title: string; onClose: () => void; children: ReactNode; testId: string; footer?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // onClose in einer Ref halten: Inline-Funktionen der Aufrufer sollen den Fokus nicht bei jedem Rendern zurücksetzen.
  const onCloseRef = useRef(props.onClose);
  useEffect(() => {
    onCloseRef.current = props.onClose;
  });
  const onClose = () => onCloseRef.current();
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, []);
  return (
    <div
      className="lfx-overlay"
      data-testid={`${props.testId}-overlay`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="lfx-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} ref={ref} data-testid={props.testId}>
        <header className="lfx-dialog__head">
          <h2 id={titleId}>{props.title}</h2>
          <button type="button" className="lfx-icon-btn" aria-label="Schließen" data-testid={`${props.testId}-close`} onClick={onClose}>
            ×
          </button>
        </header>
        <div className="lfx-dialog__body">{props.children}</div>
        {props.footer && <footer className="lfx-dialog__foot">{props.footer}</footer>}
      </div>
    </div>
  );
}

/** Einmalig sichtbares Geheimnis (Token, Webhook-URL, Secret) mit Kopierknopf. */
export function SecretReveal(props: { label: string; value: string; testId: string; note?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="lfx-secret" data-testid={props.testId}>
      <div className="lfx-secret__label">{props.label}</div>
      <div className="lfx-secret__row">
        <code className="lfx-secret__value" data-testid={`${props.testId}-value`}>
          {props.value}
        </code>
        <Button
          testId={`${props.testId}-copy`}
          onClick={() => {
            void copyText(props.value).then(setCopied);
          }}
        >
          {copied ? 'Kopiert' : 'Kopieren'}
        </Button>
      </div>
      <div className="lfx-secret__note">{props.note ?? 'Wird nur jetzt angezeigt – sicher aufbewahren.'}</div>
    </div>
  );
}

export function Tabs<T extends string>(props: { value: T; onChange: (v: T) => void; items: { value: T; label: string }[]; testId: string }) {
  return (
    <div className="lfx-tabs" role="tablist" data-testid={props.testId}>
      {props.items.map((it) => (
        <button
          key={it.value}
          type="button"
          role="tab"
          aria-selected={props.value === it.value}
          className={`lfx-tab${props.value === it.value ? ' lfx-tab--active' : ''}`}
          data-testid={`${props.testId}-${it.value}`}
          onClick={() => props.onChange(it.value)}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

/** Toasts unten rechts (Erinnerungen, Export fertig …). */
/** Timer je Toast-ID genau einmal anlegen (neue Toasts setzen ältere nicht zurück). */
function useToastTimers(toasts: Toast[], dismiss: (id: string) => void) {
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const map = timers.current;
    const ids = new Set(toasts.map((t) => t.id));
    for (const [id, timer] of map) {
      if (!ids.has(id)) {
        clearTimeout(timer);
        map.delete(id);
      }
    }
    for (const t of toasts) {
      if (t.timeout > 0 && !map.has(t.id)) map.set(t.id, setTimeout(() => dismiss(t.id), t.timeout));
    }
  }, [toasts, dismiss]);
  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach(clearTimeout);
      map.clear();
    };
  }, []);
}

/** Clientseitig navigieren ohne Router-Kontext (BrowserRouter hört auf popstate). */
function navigateOutsideRouter(href: string) {
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function ToastList(props: { standalone?: boolean }) {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  useToastTimers(toasts, dismiss);
  if (!toasts.length) return null;
  return (
    <div className="lfx-toasts" aria-live="polite" data-testid="extras-toasts">
      {toasts.map((t) => (
        <div key={t.id} className={`lfx-toast lfx-toast--${t.kind}`} role={t.kind === 'error' ? 'alert' : 'status'} data-testid={`extras-toast-${t.kind}`}>
          <div className="lfx-toast__text">
            <strong>{t.title}</strong>
            {t.body && <div className="lfx-toast__body">{t.body}</div>}
          </div>
          <div className="lfx-toast__actions">
            {t.href &&
              (props.standalone ? (
                <a
                  href={t.href}
                  className="lfx-link"
                  data-testid="extras-toast-link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateOutsideRouter(t.href as string);
                    dismiss(t.id);
                  }}
                >
                  Öffnen
                </a>
              ) : (
                <Link to={t.href} className="lfx-link" data-testid="extras-toast-link" onClick={() => dismiss(t.id)}>
                  Öffnen
                </Link>
              ))}
            {t.action && (
              <Button
                testId="extras-toast-action"
                onClick={() => {
                  t.action?.run();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </Button>
            )}
            <button type="button" className="lfx-icon-btn" aria-label="Schließen" data-testid="extras-toast-close" onClick={() => dismiss(t.id)}>
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Toasts im App-Baum (Teil von `extraGlobals`, innerhalb des Routers). */
export function ExtrasToaster() {
  useEffect(() => {
    useToastStore.setState((s) => ({ hosts: s.hosts + 1 }));
    return () => useToastStore.setState((s) => ({ hosts: s.hosts - 1 }));
  }, []);
  return <ToastList />;
}
